import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import DailyResetCard from "../components/DailyResetCard";
import Trackers from "../components/Trackers";
import MicroAction from "../components/MicroAction";
import { Sparkles, Wand2, Undo2 } from "lucide-react";
import { getResetForState, getUniversalReset } from "../data/stateBasedResets";
import { getTodaysMicroAction } from "../data/microActions";
import { useAuth } from "../lib/auth-context";
import { api } from "../lib/api";
import { toast } from "sonner";

const Home = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isPro, trialActive, trialDaysRemaining } = useAuth();
  const [streak, setStreak] = useState(0);
  // Tracks ONLY the current in-flight session's completion (not "did user complete any reset today").
  // This lets Pro users start additional resets without the card being disabled.
  const [sessionDone, setSessionDone] = useState(false);
  const [hasCompletedAnyToday, setHasCompletedAnyToday] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState(null);
  // Bump this when starting a new session to force-remount DailyResetCard
  // (clears journal inputs, resets local state — guarantees a fresh session).
  const [sessionInstance, setSessionInstance] = useState(0);
  const [reset, setReset] = useState(getUniversalReset());
  const [trackers, setTrackers] = useState({ mood: null, energy: null, sleep: "", hydration: 0 });
  const microAction = getTodaysMicroAction();

  const stateParam = searchParams.get("state");

  useEffect(() => {
    if (isPro && stateParam) {
      setReset(getResetForState(stateParam));
    } else {
      setReset(getUniversalReset());
    }
    // Any state change implies a fresh session intent — reset local completion.
    setSessionDone(false);
    setActiveSessionId(null);
    setSessionInstance((v) => v + 1);
  }, [isPro, stateParam]);

  useEffect(() => {
    (async () => {
      try {
        const [doneRes, analyticsRes, trackerRes] = await Promise.all([
          api.isDoneToday(),
          api.myAnalytics(),
          api.getTrackerToday(),
        ]);
        setHasCompletedAnyToday(doneRes.data.is_done_today);
        // Only block initial render if Free user already done today.
        if (!isPro && doneRes.data.is_done_today) {
          setSessionDone(true);
        }
        setStreak(analyticsRes.data.current_streak || 0);
        const t = trackerRes.data;
        setTrackers({
          mood: t.mood ?? null,
          energy: t.energy ?? null,
          sleep: t.sleep != null ? String(t.sleep) : "",
          hydration: t.hydration ?? 0,
        });
      } catch (e) {
        // silent
      }
    })();
    // intentional: run on mount + when pro status changes
  }, [isPro]);

  const handleMarkDone = async ({ journalIn, journalOut }) => {
    try {
      const payload = {
        state: isPro && stateParam ? stateParam : null,
        reset_id: reset.id || 0,
        breathwork_name: reset.breathwork.name,
        mindset: reset.mindset,
        affirmation: reset.affirmation,
        journal_prompt_in: reset.journalIn,
        journal_prompt_out: reset.journalOut,
        journal_answer_in: journalIn || "",
        journal_answer_out: journalOut || "",
        completed: true,
      };
      const res = await api.createSession(payload);
      setActiveSessionId(res.data.id);
      setSessionDone(true);
      setHasCompletedAnyToday(true);
      const analytics = await api.myAnalytics();
      setStreak(analytics.data.current_streak || 0);
      toast.success(`Reset complete · ${analytics.data.current_streak} day${analytics.data.current_streak === 1 ? "" : "s"} streak`, {
        description: "Saved to Journal History.",
      });
    } catch (e) {
      const msg = e.response?.data?.detail || "Could not save session";
      toast.error(msg);
    }
  };

  const handleUndo = async () => {
    const idToUndo = activeSessionId;
    try {
      if (idToUndo) {
        await api.updateSessionCompletion(idToUndo, false);
      } else {
        const r = await api.listSessions();
        const latest = r.data.items?.find((s) => s.completed);
        if (latest) await api.updateSessionCompletion(latest.id, false);
      }
      setSessionDone(false);
      setActiveSessionId(null);
      const doneRes = await api.isDoneToday();
      setHasCompletedAnyToday(doneRes.data.is_done_today);
      const analytics = await api.myAnalytics();
      setStreak(analytics.data.current_streak || 0);
      toast("Completion undone");
    } catch (e) { toast.error("Undo failed"); }
  };

  const handleTryAnother = () => {
    // Pro-only: clear local completion, bump instance to force a fresh card.
    setSessionDone(false);
    setActiveSessionId(null);
    setSessionInstance((v) => v + 1);
    setSearchParams({});
    navigate("/state-selector");
  };

  const handleTrackerChange = async (next) => {
    setTrackers(next);
    try {
      await api.saveTrackerToday({
        mood: next.mood,
        energy: next.energy,
        sleep: next.sleep ? parseFloat(next.sleep) : null,
        hydration: next.hydration ?? 0,
      });
    } catch (e) { /* silent */ }
  };

  return (
    <div className="app-shell" data-testid="home-page">
      <Header streak={streak} />

      {trialActive && (
        <div className="px-6 mb-2" data-testid="trial-banner">
          <div className="rounded-2xl bg-gradient-to-br from-[#234B38] to-[#1A382A] text-white px-4 py-3 flex items-center gap-3">
            <Sparkles className="w-4 h-4 text-[#F5D0D6] shrink-0" />
            <p className="text-xs leading-snug flex-1">
              <span className="font-semibold">Pro trial active</span> — {trialDaysRemaining} day{trialDaysRemaining === 1 ? "" : "s"} left.
            </p>
          </div>
        </div>
      )}

      {isPro && (
        <div className="px-6 mb-2">
          <button
            onClick={() => navigate("/state-selector")}
            data-testid="open-state-selector-btn"
            className="w-full rounded-2xl bg-white border border-[#E2EBE6] hover:bg-[#F5D0D6]/30 px-4 py-3 flex items-center gap-3 transition-colors"
          >
            <Wand2 className="w-4 h-4 text-[#234B38]" />
            <span className="text-sm text-[#1A2E24] flex-1 text-left">
              {stateParam ? `Tailored for ${stateParam.replace("_", " ")}` : "Pick a state for today"}
            </span>
            <span className="text-[10px] text-[#5C7568] uppercase tracking-wider">Pro</span>
          </button>
        </div>
      )}

      <DailyResetCard
        key={sessionInstance}
        reset={reset}
        doneToday={sessionDone}
        onMarkDone={handleMarkDone}
      />

      {sessionDone && (
        <div className="px-6 -mt-2 mb-2 flex gap-3" data-testid="after-done-actions">
          <button
            onClick={handleUndo}
            data-testid="undo-completion-btn"
            className="flex-1 rounded-full bg-white border border-[#E2EBE6] hover:bg-[#F5D0D6]/30 text-[#234B38] py-3 font-medium flex items-center justify-center gap-2 transition-colors"
          >
            <Undo2 className="w-4 h-4" /> Undo
          </button>
          {isPro && (
            <button
              onClick={handleTryAnother}
              data-testid="try-another-btn"
              className="flex-1 rounded-full bg-[#234B38] hover:bg-[#1A382A] text-white py-3 font-medium flex items-center justify-center gap-2 transition-colors"
            >
              <Sparkles className="w-4 h-4" /> Another reset
            </button>
          )}
        </div>
      )}

      <Trackers initial={trackers} isProUser={isPro} onChange={handleTrackerChange} />
      <MicroAction action={microAction} />
    </div>
  );
};

export default Home;
